/**
 * unifiedInbox/mappers — PURE mappers + status-derivation for the Unified
 * Inbox (architecture-ux-restructure.md §6.3, DB-Mapping §10).
 *
 * All functions here are pure (no I/O, no React) — trivially unit-testable
 * against the canonical §6.3 I/O-pairs (13 status cases) and the §10
 * DB-mapping rules (Cents / epoch-ms conventions). None of these functions
 * throw: missing/invalid input degrades to `null`/`''`/`0`/`[]`/a neutral
 * status — NEVER synthesized, NEVER guessed (Golden Rule: fail-closed, no
 * silent fallbacks; §7 "Pure Funktionen werfen NIE").
 *
 * @module unifiedInbox/mappers
 */

import type { Timestamp } from 'firebase/firestore';
import type { ReceivedInvoice } from '../../types/receivedInvoiceTypes';
import type { InboundDocument, InboundProcessingStatus, InboundSource } from '../../types/inboundTypes';
import type { ScannedReceipt } from '../../types/scannedReceiptTypes';
import type { Expense } from '../../types/expenseTypes';
import { eurosToCents } from '../moneyUtils';
import { deriveInboundBadges } from '../inboundAdapters';
import type {
  ExpenseLink,
  ExpenseLinkIndex,
  UnifiedChannel,
  UnifiedInboxItem,
  UnifiedStatus,
} from './types';

// ─── Small defensive primitives (pattern: inboundAdapters.ts num()/str()) ─────

/** Non-empty (trimmed) string, else `null`. */
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

/** String, else `''` (used where the target field is non-nullable, e.g. `currency`). */
function strOrEmpty(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/**
 * EUR-Dezimal → Integer-Cents (§10). `non-finite ⇒ null` — `eurosToCents()`
 * itself throws on non-finite input, so the finiteness check happens here,
 * BEFORE calling it, keeping this mapper layer throw-free.
 */
function toGrossCentsFromEuros(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? eurosToCents(v) : null;
}

/**
 * Already-integer-Cents field (`scanned_receipts.totalGrossCents`) — 1:1,
 * KEINE Doppelkonvertierung (§10 Cent-Disziplin-Doku).
 */
function centsOrNull(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** `received_invoices.uploadedAt` (ISO-String) → epoch-ms; invalid ⇒ 0 (§10). */
function isoToMs(v: string): number {
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : 0;
}

/**
 * Firestore `Timestamp` → epoch-ms, defensive against `null`/a missing
 * `toDate` method (Muster `inboundAdapters.ts:118-121`). Covers both the
 * documented `inbound_documents.receivedAt: Timestamp | null` case and the
 * §6.3 edge-case "Timestamp-Objekt ohne toDate".
 */
function timestampToMs(ts: Timestamp | null | undefined): number {
  if (ts && typeof (ts as { toDate?: unknown }).toDate === 'function') {
    return ts.toDate().getTime();
  }
  return 0;
}

// ─── buildExpenseLinkIndex ─────────────────────────────────────────────────

/**
 * Builds the cross-channel "Verbucht"-Index from a single `expenses` read
 * (§6.1/§6.3): `sourceReceivedInvoiceId ∪ sourceScannedReceiptId → {expenseId,
 * status}`. `sourceReceivedInvoiceId` doubles as the key for BOTH
 * `received_invoices` docs and `inbound_documents` docs (the booking modal
 * reuses the inbound doc's own id as `invoice.id`, `inboundAdapters.ts:115-124`
 * + `ExpenseBookingModal.tsx:232`) — one field, two channels.
 *
 * Expense-Korrektur-Docs verfälschen den Index NICHT: sie tragen
 * `sourceReceivedInvoiceId: null` und lassen `sourceScannedReceiptId`
 * unset (`utils/expenseCorrection.ts:213-217`), also nie ein Treffer.
 *
 * If more than one expense resolves to the same source id (should not
 * happen in normal operation — a source is booked at most once), the last
 * entry in `expenses` order wins; callers pass `getExpensesForUser()`'s
 * `expenseDate desc` order, so this is a documented, low-risk edge case.
 *
 * @param expenses all expenses for the current scope (unfiltered).
 * @returns a read-only lookup map.
 */
export function buildExpenseLinkIndex(expenses: Expense[]): ExpenseLinkIndex {
  const map = new Map<string, ExpenseLink>();

  for (const expense of expenses) {
    const link: ExpenseLink = {
      expenseId: expense.id,
      status: expense.status === 'booked' ? 'booked' : 'draft',
    };

    const receivedOrInboundId = strOrNull(expense.sourceReceivedInvoiceId);
    if (receivedOrInboundId) {
      map.set(receivedOrInboundId, link);
    }

    const scannedId = strOrNull(expense.sourceScannedReceiptId);
    if (scannedId) {
      map.set(scannedId, link);
    }
  }

  return map;
}

// ─── deriveUnifiedStatus ────────────────────────────────────────────────────

/**
 * Discriminated input for {@link deriveUnifiedStatus} — one variant per
 * source collection, carrying only the fields the status derivation needs
 * (the collection's own status/processingStatus field + the id used to
 * look itself up in the {@link ExpenseLinkIndex}).
 */
export type UnifiedStatusInput =
  | { source: 'scanned'; status: ScannedReceipt['status']; sourceId: string }
  | { source: 'inbound'; processingStatus: InboundProcessingStatus; sourceId: string }
  | { source: 'received'; sourceId: string; discardedAt?: string | null };

/**
 * Derives the kanalunabhängige {@link UnifiedStatus} — the canonical §6.3
 * I/O-table (13 cases), the Tester-Vertrag for this package:
 *
 * | Quelle | Input | → status |
 * |---|---|---|
 * | scanned | `status:'extracting'` | `processing` |
 * | scanned | `status:'failed'` | `failed` |
 * | scanned | `status:'extracted'`, kein Index-Treffer | `new` |
 * | scanned | `status:'extracted'`, Index `{status:'draft'}` | `draft` |
 * | scanned | `status:'booked'` (Index egal) | `booked` |
 * | scanned | `status:'discarded'` | `discarded` |
 * | inbound | `processingStatus` ∈ {`archived_pending_parse`,`deferred`} | `processing` |
 * | inbound | `processingStatus:'failed_parse'` | `failed` |
 * | inbound | `processingStatus:'processed'`, kein Index-Treffer | `new` |
 * | inbound | `processingStatus:'processed'`, Index `{status:'booked'}` | `booked` |
 * | received | kein Index-Treffer | `new` |
 * | received | Index `{status:'draft'}` | `draft` |
 * | received | Index `{status:'booked'}` | `booked` |
 * | received | `discardedAt` gesetzt, kein Index/Index draft | `discarded` (NEU, M-V4-D2) |
 * | received | `discardedAt` gesetzt, Index `{status:'booked'}` | `booked` (booked gewinnt) |
 *
 * Implementation note (documented assumption, not itself one of the 13
 * canonical rows): for the two non-terminal source states that DO consult
 * the index (`scanned.extracted`, `inbound.processed`) as well as `received`,
 * an index treffer with `{status:'draft'}` always yields `draft` and
 * `{status:'booked'}` always yields `booked` — the SAME rule that rows
 * 3/4/11/12/13 already state explicitly. This only additionally covers the
 * un-enumerated "inbound processed + index draft" combination the table
 * leaves implicit; it does not change the behaviour of any of the 13 listed
 * rows.
 *
 * @param input the discriminated status-relevant fields of the source doc.
 * @param index the {@link ExpenseLinkIndex} built via {@link buildExpenseLinkIndex}.
 * @returns the derived {@link UnifiedStatus}.
 */
export function deriveUnifiedStatus(
  input: UnifiedStatusInput,
  index: ExpenseLinkIndex,
): UnifiedStatus {
  switch (input.source) {
    case 'scanned': {
      if (input.status === 'extracting') return 'processing';
      if (input.status === 'failed') return 'failed';
      if (input.status === 'discarded') return 'discarded';
      if (input.status === 'booked') return 'booked';
      // 'extracted'
      return statusFromIndex(index.get(input.sourceId));
    }
    case 'inbound': {
      if (
        input.processingStatus === 'archived_pending_parse' ||
        input.processingStatus === 'deferred'
      ) {
        return 'processing';
      }
      if (input.processingStatus === 'failed_parse') return 'failed';
      // 'processed'
      return statusFromIndex(index.get(input.sourceId));
    }
    case 'received': {
      // M-V4-D2 (§4.3.4): `booked` gewinnt IMMER (defensiv gegen den
      // Discard-Wettlauf, Edge #10), DANN Soft-Discard, sonst Bestandslogik.
      const link = index.get(input.sourceId);
      if (link?.status === 'booked') return 'booked';
      if (input.discardedAt) return 'discarded';
      return statusFromIndex(link);
    }
    default: {
      // Exhaustiveness guard — fail-closed neutral, never throws.
      return 'new';
    }
  }
}

/** `undefined` ⇒ `new`; otherwise mirrors the linked expense's own status. */
function statusFromIndex(link: ExpenseLink | undefined): UnifiedStatus {
  if (!link) return 'new';
  return link.status === 'booked' ? 'booked' : 'draft';
}

// ─── channel helpers ────────────────────────────────────────────────────────

/**
 * `inbound.source==='upload'|'claim'` wird als Kanal "Upload" gelabelt,
 * `'email'` als "E-Mail" (§6.1).
 */
function inboundChannel(source: InboundSource): UnifiedChannel {
  return source === 'email' ? 'email' : 'upload';
}

// ─── mapReceived ────────────────────────────────────────────────────────────

/**
 * Maps a `received_invoices` document (Datei-Upload-Kanal) onto a
 * {@link UnifiedInboxItem}.
 *
 * @param doc the received-invoice document.
 * @param index the cross-channel {@link ExpenseLinkIndex}.
 * @returns the merged view-layer item.
 */
export function mapReceived(
  doc: ReceivedInvoice,
  index: ExpenseLinkIndex,
): UnifiedInboxItem {
  const status = deriveUnifiedStatus(
    { source: 'received', sourceId: doc.id, discardedAt: doc.discardedAt ?? null },
    index,
  );
  const link = index.get(doc.id);

  return {
    key: `received_invoices:${doc.id}`,
    sourceCollection: 'received_invoices',
    sourceId: doc.id,
    channel: 'upload',
    receivedAtMs: isoToMs(doc.uploadedAt),
    supplierName: strOrNull(doc.data?.sellerName),
    documentNo: strOrNull(doc.data?.documentNo),
    grossCents: toGrossCentsFromEuros(doc.data?.grandTotal),
    currency: strOrEmpty(doc.data?.currency),
    status,
    linkedExpenseId: link?.expenseId ?? null,
    qualityBadges: [],
    raw: doc,
  };
}

// ─── mapInbound ─────────────────────────────────────────────────────────────

/**
 * Maps an `inbound_documents` document (E-Mail/Loop-A-Upload-Kanal) onto a
 * {@link UnifiedInboxItem}. `receivedAt: null` ⇒ `receivedAtMs: 0` (§6.3
 * edge-case, sortiert ans Listenende via `mergeAndSort`). `extracted === null`
 * ⇒ every extracted-derived field is `null`/`''` (§10, §6.3 edge-case).
 *
 * @param doc the inbound document.
 * @param index the cross-channel {@link ExpenseLinkIndex}.
 * @returns the merged view-layer item.
 */
export function mapInbound(
  doc: InboundDocument,
  index: ExpenseLinkIndex,
): UnifiedInboxItem {
  const status = deriveUnifiedStatus(
    { source: 'inbound', processingStatus: doc.processingStatus, sourceId: doc.id },
    index,
  );
  const link = index.get(doc.id);
  const ex = doc.extracted;

  return {
    key: `inbound_documents:${doc.id}`,
    sourceCollection: 'inbound_documents',
    sourceId: doc.id,
    channel: inboundChannel(doc.source),
    receivedAtMs: timestampToMs(doc.receivedAt),
    supplierName: ex ? strOrNull(ex.sellerName) : null,
    documentNo: ex ? strOrNull(ex.invoiceNumber) : null,
    grossCents: ex ? toGrossCentsFromEuros(ex.grossTotal) : null,
    currency: ex ? strOrEmpty(ex.currency) : '',
    status,
    linkedExpenseId: link?.expenseId ?? null,
    qualityBadges: deriveInboundBadges(doc),
    raw: doc,
  };
}

// ─── mapScanned ─────────────────────────────────────────────────────────────

/**
 * Maps a `scanned_receipts` document (Foto/Scan-Kanal) onto a
 * {@link UnifiedInboxItem}. `totalGrossCents` is already Integer-Cents — 1:1
 * durchgereicht, KEINE Doppelkonvertierung (§10).
 *
 * @param doc the scanned-receipt document.
 * @param index the cross-channel {@link ExpenseLinkIndex}.
 * @returns the merged view-layer item.
 */
export function mapScanned(
  doc: ScannedReceipt,
  index: ExpenseLinkIndex,
): UnifiedInboxItem {
  const status = deriveUnifiedStatus({ source: 'scanned', status: doc.status, sourceId: doc.id }, index);
  const link = index.get(doc.id);

  return {
    key: `scanned_receipts:${doc.id}`,
    sourceCollection: 'scanned_receipts',
    sourceId: doc.id,
    channel: 'scan',
    receivedAtMs: timestampToMs(doc.createdAt),
    supplierName: strOrNull(doc.extraction?.supplierName),
    documentNo: strOrNull(doc.extraction?.invoiceNumber),
    grossCents: centsOrNull(doc.totalGrossCents),
    currency: '', // ScannedReceipt trägt kein eigenes Währungsfeld (MVP: EUR-only, §8.4 architecture-m13a).
    status,
    linkedExpenseId: link?.expenseId ?? null,
    qualityBadges: [],
    raw: doc,
  };
}

// ─── mergeAndSort ───────────────────────────────────────────────────────────

/**
 * Flattens per-source item arrays into ONE list, sorted `receivedAtMs`
 * descending (newest first); ties broken by `key` ascending for a
 * deterministic, stable order (§6.3).
 *
 * @param itemGroups arrays of {@link UnifiedInboxItem}, one per source (or
 *   already-merged batches — the function only cares about the flattened
 *   result).
 * @returns a single, sorted array.
 */
export function mergeAndSort(itemGroups: UnifiedInboxItem[][]): UnifiedInboxItem[] {
  // Manual flatten (not Array.prototype.flat) — avoids relying on an ES2019
  // runtime method under the project's `target: es5` CRA build.
  const flat: UnifiedInboxItem[] = ([] as UnifiedInboxItem[]).concat(...itemGroups);
  return flat.sort((a, b) => {
    if (b.receivedAtMs !== a.receivedAtMs) return b.receivedAtMs - a.receivedAtMs;
    if (a.key < b.key) return -1;
    if (a.key > b.key) return 1;
    return 0;
  });
}
